import { HttpStatusCode } from "axios";
import { toast } from "react-toastify";

/**
 * Translates an API error into a user-friendly `Error` object.
 *
 * @param err - The raw error object returned from an API request.
 * @returns A normalized `Error` object with a user-friendly message.
 *
 * - If the error contains a response message, it is returned.
 * - If the error indicates a timeout, a timeout-specific message is returned.
 * - Otherwise, a generic error message is returned.
 */
export function translateError(err: any): Error {
    if (err?.response?.data?.message) return new Error(err.response.data.message);
    if (err?.message?.includes("timeout")) return new Error("The request timed out. Please retry.");
    return new Error("Something went wrong. Please try again.");
}

/**
 * Handles API errors and displays them via toast notifications.
 *
 * @param error - The error object received from the API response.
 *
 * - If the response contains validation errors (`400 Bad Request`),
 *   each error message will be displayed as a toast notification.
 * - Otherwise, a generic "unexpected error" toast is shown.
 */
export function handleApiError(error: any) {
    if (error?.status === HttpStatusCode.BadRequest && error.data?.errors) {
        const errors = error.data.errors;

        Object.entries(errors).forEach(([field, messages]) => {
            (messages as string[]).forEach((message) => {
                toast.error(`${message}`, {
                    autoClose: 5000,
                });
            });
        });
    }
    else {
        toast.error("An unexpected error occurred.");
    }
}
